/**
 * @export
 * @class Fullpage
 */
import 'fullpage.js/vendors/scrolloverflow.js';
import { debounce } from 'ts-debounce';
// @ts-ignore
import * as fullpage from 'fullpage.js'; // tslint:disable-line

import { get, getAll, getParameterByName, isBreakpoint, toArray } from '../../helpers/utilities';

export class Fullpage {
    private breakpoint = '';
    private container: Element;
    private fpInstance!: FullPageJs;
    private desktopView: Element;
    private mobileViews: Element[];

    
    /**
     * Creates an instance of Global.
     * 
     * @memberof Home
     */
    constructor() {
        this.container   = get('#js-fullpage') as Element;
        this.desktopView = get('#js-home__products-split-desktop') as Element;
        this.mobileViews = getAll('.js-home__products-split-mobile') as Element[];

        this.initFullPage();
    }


    /**
     * Initiates the fullpage.js intance for this page.
     * 
     * @private
     * @return void
     */
    private initFullPage(): void {

        // Inititiate Fullpage.js
        this.fpInstance = new fullpage('#js-fullpage', {
            scrollOverflow: true,
            recordHistory: false,
            lockAnchors: true,
            licenseKey: 'E9451320-3A38466E-9E4F68B3-C41541AF',
            onLeave: (origin: fullpage.section, destination: fullpage.section) => {
                this.onSlideLeave(origin, destination);
            },
            afterRender: () => {
                this.afterRender();

                if(getParameterByName('mailing_list')) {
                    this.fpInstance.moveTo('footer');
                }
            }
        });
    }


    /**
     * Handles our after render event for Fullpage.
     * 
     * @private
     * @return void
     */
    private afterRender(): void {
        
        // Toggle mobile products split
        if(this.mobileViews && this.desktopView) {
            this.toggleProductsSplit();
            window.addEventListener('resize', debounce(() => {
                this.toggleProductsSplit();
            }, 300));
        }
    }


    /**
     * Handles our on leave event for Fullpage.
     * 
     * @private
     * @return void
     * @param origin The starting section.
     * @param destination The new section.
     */
    private onSlideLeave({}, destination: fullpage.section): void {
        this.toggleSiteHeader(destination.index);
    }


    /**
     * Toggles the fixed style for the site header
     * 
     * @private
     * @return void
     * @param sectionIndex The index of the new section
     */
    private toggleSiteHeader(sectionIndex: number): void {
        if(sectionIndex === 0) {
            window.app.modules.site_header.container.classList.remove('c-site-header--is-fixed');
        } else {
            window.app.modules.site_header.container.classList.add('c-site-header--is-fixed');
        }
    }


    /**
     * Toggles the products split display for mobile and desktop.
     * 
     * @private
     * @return void
     * @param sectionIndex The index of the new section
     */
    private toggleProductsSplit(): void {
        let index               = 0;
        const currentBreakpoint = isBreakpoint('tablet') ? 'mobile' : 'desktop';
        if(this.breakpoint !== currentBreakpoint) {
            this.breakpoint  = currentBreakpoint;

            if(this.breakpoint === 'mobile') {
                

                // Set the current index.
                index = toArray(this.container.children).indexOf(get('.fp-section.active')) + 1;

                // Show the mobile views
                this.mobileViews.forEach(view => {
                    this.container.insertBefore(view, this.desktopView);
                    view.classList.remove('u-hidden');
                });
                
                // Hide the desktop view.
                document.body.appendChild(this.desktopView);
                this.desktopView.classList.add('u-hidden');
            } else {

                // Set the current index.
                const sections    = toArray(this.container.children);
                const activeIndex = sections.indexOf(get('.fp-section.active'));
                index = sections[activeIndex] === this.mobileViews[0] ? activeIndex + 1 : activeIndex;

                // Show the desktop view.
                this.container.insertBefore(this.desktopView, this.mobileViews[0]);
                this.desktopView.classList.remove('u-hidden');

                // Hide the mobile views
                this.mobileViews.forEach(view => {
                    document.body.appendChild(view);
                    view.classList.add('u-hidden');
                });
            }

            // Rebuild.
            this.fpInstance.reBuild();
            
            this.fpInstance.silentMoveTo(index);
        }
    }
}
